"use client";
import React from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import NextLink from "next/link";

const ZaynHowItWorksSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const rows = t("zaynHowItWorks.table.rows", { returnObjects: true }) || [];

  return (
    <Box sx={{ direction: isArabic ? "rtl" : "ltr", backgroundColor: "#F7F4FD" }}>
      {/* Comparison table block */}
      <Box sx={{ py: { xs: 5, sm: 6, md: 9 } }}>
        <Container maxWidth="md">
          <Typography
            sx={{
              fontSize: { xs: "11px", sm: "12px" },
              fontWeight: 600,
              color: "#7A2BF5",
              textTransform: "uppercase",
              letterSpacing: "1px",
              fontFamily: "var(--font-outfit)",
              textAlign: "center",
              mb: { xs: 1.5, sm: 2 },
            }}
          >
            {t("zaynHowItWorks.label")}
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "22px", sm: "26px", md: "30px" },
              fontWeight: 700,
              lineHeight: 1.3,
              color: "#14122B",
              fontFamily: "var(--font-plusjakarta)",
              textAlign: "center",
              mb: { xs: 3, sm: 4, md: 5 },
            }}
          >
            {t("zaynHowItWorks.title")}
          </Typography>

          <Box
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 4px 24px rgba(122,43,245,0.06)",
            }}
          >
            {/* Header row */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                bgcolor: "#14122B",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "10px", sm: "11px" },
                  fontWeight: 500,
                  color: "#B6A9DF",
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  fontFamily: "var(--font-outfit)",
                  px: { xs: 2.5, sm: 3, md: 4 },
                  py: { xs: 1.75, md: 2.25 },
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {t("zaynHowItWorks.table.oldWay")}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "10px", sm: "11px" },
                  fontWeight: 500,
                  color: "#FFFFFF",
                  textTransform: "uppercase",
                  letterSpacing: "1.5px",
                  fontFamily: "var(--font-outfit)",
                  px: { xs: 2.5, sm: 3, md: 4 },
                  py: { xs: 1.75, md: 2.25 },
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {t("zaynHowItWorks.table.zaynWay")}
              </Typography>
            </Box>

            {/* Data rows */}
            {rows.map((row, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  borderTop: "1px solid #F3EEFB",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "12px", sm: "13px", md: "13.5px" },
                    color: "#8A88A3",
                    fontWeight: 400,
                    fontFamily: "var(--font-outfit)",
                    backgroundColor: "#FFFFFF",
                    px: { xs: 2.5, sm: 3, md: 4 },
                    py: { xs: 1.75, md: 2.25 },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {row.oldWay}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "12px", sm: "13px", md: "13.5px" },
                    color: "#14122B",
                    fontWeight: 600,
                    fontFamily: "var(--font-outfit)",
                    backgroundColor: "#FBF8FF",
                    px: { xs: 2.5, sm: 3, md: 4 },
                    py: { xs: 1.75, md: 2.25 },
                    textAlign: isArabic ? "right" : "left",
                  }}
                >
                  {row.zaynWay}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Dark CTA banner */}
      <Box sx={{ bgcolor: "#F4F4F4", py: { xs: 4, sm: 5, md: 9 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              bgcolor: "#14122B",
              borderRadius: "20px",
              px: { xs: 3, sm: 4, md: 5 },
              py: { xs: 3, sm: 3.5, md: 4 },
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              justifyContent: "space-between",
              gap: { xs: 2.5, sm: 3 },
              direction: isArabic ? "rtl" : "ltr",
            }}
          >
            {/* Left - heading + subtext stacked */}
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: { xs: "20px", sm: "25px", md: "27px" },
                  fontWeight: 700,
                  lineHeight: 1.3,
                  color: "#FFFFFF",
                  fontFamily: "var(--font-plusjakarta)",
                  textAlign: isArabic ? "right" : "left",
                  mb: { xs: 0.8, sm: 1 },
                }}
              >
                {t("zaynHowItWorks.banner.heading")}
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: "12px", sm: "12px", md: "13px" },
                  color: "#B8B8CC",
                  lineHeight: 1.6,
                  fontWeight: 400,
                  fontFamily: "var(--font-outfit)",
                  textAlign: isArabic ? "right" : "left",
                }}
              >
                {t("zaynHowItWorks.banner.subtext")}
              </Typography>
            </Box>

            {/* Right - CTA */}
            <NextLink href="/free-trial-of-ai-recruiting-tool" passHref legacyBehavior>
              <Button
                component="a"
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#14122B",
                  px: { xs: 2, sm: 2.5 },
                  py: { xs: 1, sm: 1.1 },
                  borderRadius: "50px",
                  textTransform: "none",
                  fontSize: { xs: "12px", sm: "13px" },
                  fontWeight: 600,
                  fontFamily: "var(--font-outfit)",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  minWidth: 0,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: "#fff",
                    transform: "scale(1.03)",
                  },
                }}
              >
                {t("zaynHowItWorks.banner.cta")}
              </Button>
            </NextLink>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ZaynHowItWorksSection;